import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";

import V2PairOrgUnit from "./v2_pair_org_unit";
import WorkingHoursCompoundSelect from "./working_hour_compound_select";
import V2TabellaTurnoGiornaliero from "./v2_tabella_visualizza_turno_giornaliero";

import { createWorkingHoursCompound } from "../services/api_working_hours";
import { getUiErrorMessage } from "../services/utils";



export default function CopyDailyShift({ token, selectedOu, onSuccess }) {
  // origine turno da copiare
  const [modelOu, setModelOu] = useState(null);

  // turno selezionato da copiare
  const [selectedWorkingHour, setSelectedWorkingHour] = useState(null);

  // nome nuovo turno (obbligatorio)
  const [newShiftName, setNewShiftName] = useState("");

  // reset quando cambia l’origine (generici <-> OU)
  useEffect(() => {
    setSelectedWorkingHour(null);
  }, [modelOu?.id]);

  const handleCreate = async () => {
    if (!selectedWorkingHour?.time_slots?.length) {
      return Swal.fire({
        icon: "warning",
        title: "Seleziona un turno",
        text: "Scegli il turno da copiare.",
      });
    }

    if (!newShiftName.trim()) {
      return Swal.fire({
        icon: "warning",
        title: "Nome obbligatorio",
        text: "Inserisci il nome del nuovo turno.",
      });
    }

    const payload = {
      name: newShiftName.trim(),
      time_slots: (selectedWorkingHour.time_slots || [])
        .slice()
        .sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""))
        .map((slot) => ({
          start_at: slot.start_at,
          end_at: slot.end_at,
          breaks: (slot.breaks || []).map((br) => ({
            start_at: br.start_at,
            end_at: br.end_at,
            is_visible: br.is_visible,
          })),
        })),
    };

    // OU destinazione: invia solo se selezionata (se generico -> ometti)
    if (selectedOu?.id) payload.ou = selectedOu.id;

    try {
      Swal.fire({
        title: "Creazione turno...",
        icon: "info",
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      await createWorkingHoursCompound(token, payload);

      Swal.close();

      await Swal.fire({
        icon: "success",
        title: "Turno creato",
        text: "La copia è stata creata correttamente.",
        confirmButtonText: "OK",
      });

      // reset locale
      setModelOu(null);
      setSelectedWorkingHour(null);
      setNewShiftName("");

      onSuccess?.();
    } catch (error) {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Errore",
        text: getUiErrorMessage(error), // ✅ qui
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <div className="mt-4 rounded-b-lg border border-gray-100 bg-gray-100/60 py-4 px-5 space-y-4">
      {/* 1) Origine turno da copiare */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <V2PairOrgUnit
          label="Scegli l'origine del turno da copiare"
          helperText="Scegli se copiare da un turno generico oppure da una unità specifica"
          value={modelOu}
          onChange={(ou) => {
            setModelOu(ou);
            setSelectedWorkingHour(null);
          }}
          includeGenericOption
          genericLabel="Turni generici (nessuna unità)"
        />
      </div>

      {/* 2) Seleziona turno da copiare */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="text-sm font-semibold uppercase text-indigo-800">Turno giornaliero da copiare</div>
        <div className="text-xs text-gray-500 mt-1">Cerca e seleziona il turno che vuoi usare per la copia</div>

        <div className="mt-3">
          <WorkingHoursCompoundSelect
            token={token}
            permission="payrolls"
            sourceOu={modelOu?.id ?? null}
            value={selectedWorkingHour}
            onChange={setSelectedWorkingHour}
          />
        </div>

        {selectedWorkingHour && (
          <div className="mt-3">
            <V2TabellaTurnoGiornaliero selectedWorkingHour={selectedWorkingHour} />
          </div>
        )}
      </div>

      {/* 3) Nome nuovo turno */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="text-sm font-semibold uppercase text-indigo-800">Nome del nuovo turno</div>
        <div className="text-xs text-gray-500 mt-1">* Inserisci il nome</div>

        <div className="mt-3">
          <input
            value={newShiftName}
            onChange={(e) => setNewShiftName(e.target.value)}
            placeholder="Es. Mattina - Magazzino / Full time / Turno 7:30"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleCreate}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          Crea turno
        </button>
      </div>
    </div>
  );
}